import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Wordmark } from '../../components/brand/Wordmark';
import { useAuth } from '../../auth/AuthContext';
import { ApiClientError } from '../../lib/api';

interface StrengthResult {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  color: string;
}

function evaluateStrength(pw: string): StrengthResult {
  if (!pw) return { score: 0, label: '', color: 'var(--color-line)' };
  let score = 0;
  if (pw.length >= 10) score++;
  if (pw.length >= 14) score++;
  if (pw.length >= 18) score++;
  if (/[^a-zA-Z]/.test(pw)) score++;
  const s = Math.min(score, 4) as 0 | 1 | 2 | 3 | 4;
  if (s <= 1) return { score: s, label: 'Too short', color: 'var(--color-danger)' };
  if (s === 2) return { score: s, label: 'OK', color: 'var(--color-warning)' };
  if (s === 3) return { score: s, label: 'Good', color: 'var(--color-success)' };
  return { score: 4, label: 'Strong', color: 'var(--color-success)' };
}

export function RegisterScreen() {
  const { register } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Phase 4 §legal — signup-time contract-formation gate. Both must
  // be true before the Create-account button enables. Server-side
  // z.literal(true) is the real enforcement; this is UX.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ageAttested, setAgeAttested] = useState(false);

  const strength = useMemo(() => evaluateStrength(password), [password]);
  // Mirror the server-side rule so the Create button is enabled when the
  // input will actually be accepted by the API.
  const meetsRequirement = password.length >= 10 && /[^a-zA-Z]/.test(password);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Phase 4 §legal — both flags are statically `true` here because
      // the Create-account button is disabled until both checkboxes
      // are checked. The server still validates with z.literal(true).
      await register(email, password, {
        termsAccepted: true,
        ageAttested: true,
      });
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 429) {
          setError("Too many registrations from this network. Try again in an hour.");
        } else if (err.status === 409) {
          setError('An account with this email already exists. Try signing in.');
        } else if (err.status === 400) {
          setError('Email or password did not pass validation. Use 10+ characters with a number or symbol.');
        } else {
          setError('Something went wrong. Try again.');
        }
      } else {
        setError('Network error. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main id="main-content" className="container" style={{ paddingBlock: 'var(--space-7)' }}>
      <div style={{ paddingBlockStart: 'var(--space-7)', paddingBlockEnd: 'var(--space-6)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Wordmark variant="stacked" size="large" />
        <p className="muted" style={{ marginBlockStart: 'var(--space-2)' }}>
          Create your account
        </p>
      </div>

      <form
        className="stack"
        onSubmit={onSubmit}
        style={{ '--stack-gap': 'var(--space-4)' } as React.CSSProperties}
      >
        <Input
          label="Email"
          type="email"
          autoComplete="username"
          inputMode="email"
          enterKeyHint="next"
          autoCapitalize="off"
          spellCheck={false}
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div className="field">
          <label className="field-label" htmlFor="pw">
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="pw"
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              enterKeyHint="go"
              required
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field-control"
              style={{ paddingInlineEnd: '60px' }}
              aria-describedby="pw-help pw-strength"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              style={{
                position: 'absolute',
                insetInlineEnd: 0,
                insetBlockStart: 0,
                blockSize: '100%',
                paddingInline: 'var(--space-3)',
                color: 'var(--color-ink-secondary)',
                fontSize: 'var(--text-caption)',
              }}
              aria-label={showPw ? 'Hide password' : 'Show password'}
            >
              {showPw ? 'Hide' : 'Show'}
            </button>
          </div>
          <div
            id="pw-strength"
            aria-live="polite"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              marginBlockStart: 'var(--space-2)',
            }}
          >
            <div
              role="presentation"
              style={{
                flex: 1,
                blockSize: 4,
                background: 'var(--color-bg-sunken)',
                borderRadius: 'var(--radius-pill)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  inlineSize: `${(strength.score / 4) * 100}%`,
                  blockSize: '100%',
                  background: strength.color,
                  transition: 'inline-size var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out)',
                }}
              />
            </div>
            {strength.label ? (
              <span className="text-caption" style={{ color: strength.color, minInlineSize: '4ch' }}>
                {strength.label}
              </span>
            ) : null}
          </div>
          <div id="pw-help" className="field-help">
            At least 10 characters, including a number or symbol.
          </div>
        </div>

        {error ? (
          <div className="field-error" role="alert">
            {error}
          </div>
        ) : null}

        {/* Phase 4 §legal — signup-time contract-formation gate (Terms/Privacy
            + 18+). Server records timestamps in users.terms_accepted_at /
            terms_version / age_attested_at via /api/auth/register. */}
        <div
          className="stack"
          style={{ '--stack-gap': 'var(--space-2)' } as React.CSSProperties}
        >
          <label
            htmlFor="terms-accepted"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
              minHeight: 44,
              padding: 'var(--space-2) 0',
              cursor: 'pointer',
            }}
          >
            <input
              id="terms-accepted"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required
              style={{ marginBlockStart: '0.25em' }}
            />
            <span>
              I agree to the{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--color-accent)' }}
              >
                Terms
              </a>{' '}
              and{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--color-accent)' }}
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>
          <label
            htmlFor="age-attested"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
              minHeight: 44,
              padding: 'var(--space-2) 0',
              cursor: 'pointer',
            }}
          >
            <input
              id="age-attested"
              type="checkbox"
              checked={ageAttested}
              onChange={(e) => setAgeAttested(e.target.checked)}
              required
              style={{ marginBlockStart: '0.25em' }}
            />
            <span>I am at least 18 years old.</span>
          </label>
        </div>

        <Button
          type="submit"
          size="lg"
          block
          loading={busy}
          disabled={!email || !meetsRequirement || !termsAccepted || !ageAttested}
        >
          Create account
        </Button>

        <div style={{ textAlign: 'center', marginBlockStart: 'var(--space-3)' }}>
          <Link to="/login" style={{ color: 'var(--color-accent)' }}>
            Already have an account? Sign in
          </Link>
        </div>
      </form>

      <p className="dim text-caption" style={{ marginBlockStart: 'var(--space-7)', textAlign: 'center' }}>
        Your data lives in a private encrypted store. Nothing is shared.
      </p>
    </main>
  );
}
